import Router from 'next/router'
import {urlParams} from 'utils/urlParams'

import {getPersistedSapiLabelWithFallback} from '@vio/core/src/_web/sapiLabel/business'
import {buildOfferCheckEndpoint} from '@vio/core/src/offer/business/buildOfferCheckEndpoint'
import {
  OfferCheckAPIResponse,
  UnifiedOfferCheckData
} from '@vio/core/src/offer/types/OfferCheck'
import {
  adaptExistingBookingRoom,
  adaptOfferCheckRoom
} from '@vio/core/src/offer/utils/adaptOfferCheckRoom'
import {UrlCheckoutParamsType} from '@vio/core/src/utils/url/types/UrlParams'

import {updatePrices} from '../../utils/updatePrices'

import type {Booking} from '@vio/core/src/api-types/bovio/response/booking'

const BoFhApi = process.env.NEXT_PUBLIC_BOFH_ENDPOINT!

const OFFER_TOKEN_URL_PARAM: keyof UrlCheckoutParamsType = 'offer_token'
export const RECHECK_METADATA_ID_URL_PARAM: keyof UrlCheckoutParamsType =
  'recheckMetadataId'

export const buildOfferCheckEndpointFromCurrentUrl = (
  anonymousId: string,
  searchId: string | null
) => {
  const currentUrlParams = urlParams()
  const label = getPersistedSapiLabelWithFallback()

  return (
    buildOfferCheckEndpoint(
      {
        ...currentUrlParams,
        anonymousId,
        searchId,
        label
      },
      BoFhApi
    ) || ''
  )
}

/**
 * Points an offer-check URL (`…/offers/{token}/check`) with a different offer token
 */
export const withOfferCheckToken = (url: string, token: string) =>
  url.replace(/\/offers\/[^/]+\/check(?=\?|$)/, `/offers/${token}/check`)

/** Syncs the response token (and recheckMetadataId, when present) into the browser URL. */
export const syncOfferCheckTokenToUrl = ({
  token,
  recheckMetadataId
}: {
  token?: string | null
  recheckMetadataId?: string | null
}) => {
  const {pathname, search, hash} = window.location
  const searchParams = new URLSearchParams(search)
  const currentToken = searchParams.get(OFFER_TOKEN_URL_PARAM)

  const previousParams = searchParams.toString()

  if (currentToken !== token && token) {
    searchParams.set(OFFER_TOKEN_URL_PARAM, token)
  }

  if (recheckMetadataId) {
    searchParams.set(RECHECK_METADATA_ID_URL_PARAM, recheckMetadataId)
  }

  const newParams = searchParams.toString()

  // Replace only if params changed
  if (newParams !== previousParams) {
    Router.replace(`${pathname}?${newParams}${hash}`, undefined, {
      shallow: true
    })
  }
}

export const offerCheckTransformResponseFunc = (
  response: OfferCheckAPIResponse
): UnifiedOfferCheckData => {
  return {
    ...response,
    rooms: response.rooms?.map(room => adaptOfferCheckRoom(room)),
    providerTraderInformation: response.providerTraderInformation,
    links: response.links,
    merchantOfRecord: response.merchantOfRecord,
    status: response.status,
    prices: updatePrices(response.prices, response.checkIn, response.checkOut),
    httpRequestId: response.httpRequestId
  }
}

//We don't really need that anymore after switching to SB v3,
//but removing requires some effort,
//additional story created [sc-145179]
export const existingBookingOfferCheckDataTransform = (
  booking: Booking
): UnifiedOfferCheckData | undefined => {
  if (!booking) return undefined

  return {
    checkIn: booking.checkIn,
    checkOut: booking.checkOut,
    booking: booking,
    cashback: booking.cashback || undefined,
    chargeDueAt: booking.chargeDueAt,
    paymentMode: booking.paymentMode,
    rooms: booking.rooms.map(room => adaptExistingBookingRoom(room)),
    merchantOfRecord: booking.merchantOfRecord,
    links: booking.links,
    status: booking.status,
    prices: updatePrices(booking.prices, booking.checkIn, booking.checkOut),
    cancellationPenalties: booking.cancellationPenalties,
    currencyConversionRate: booking.currencyConversionRate,
    bookingInfo: {
      customerSupport: booking.customerSupport,
      billing: booking.billing,
      confirmationId: booking.confirmationId,
      customer: booking.customer,
      id: booking.id,
      source: booking.source,
      expiresAt: booking.expiresAt,
      payment: booking.payment
    }
  }
}
